import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { merge, Subject, takeUntil, tap } from 'rxjs';
import { take } from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import { NotificationsController } from '@core/user-interface-legacy';
import {
  AssignedFlowsActions,
  DuplicateViewContext,
  RequisitionAssignmentsActions,
  RequisitionsActions,
  UpdateContext,
} from '@recruitee/requisitions-data';

@Injectable()
export class RequisitionsNotificationsService {
  private readonly actions$ = inject(Actions);
  private readonly notifications = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);

  private ngOnDestroy$ = new Subject<void>();

  private texts = {
    savedAsDraft: this.i18n.translate('app.requisitions.notifications.saved_as_draft'),
    changesSaved: this.i18n.translate('app.requisitions.notifications.changes_saved'),
    titleSaved: this.i18n.translate('app.requisitions.notifications.title_saved'),
    approvalRequested: this.i18n.translate('app.requisitions.notifications.approval_requested'),
    requestCanceled: this.i18n.translate('app.requisitions.notifications.request_canceled'),
    requestApproved: this.i18n.translate('app.requisitions.notifications.request_approved'),
    requestRejected: {
      title: this.i18n.translate('app.requisitions.notifications.request_rejected.title'),
      message: this.i18n.translate('app.requisitions.notifications.request_rejected.message'),
    },
    followTitle: this.i18n.translate('app.requisitions.follow_success.title.one'),
    followDescription: this.i18n.translate('app.requisitions.follow_success.description.one'),
    unfollowTitle: this.i18n.translate('app.requisitions.unfollow_success.title.one'),
    duplicated: {
      title: this.i18n.translate('app.requisitions.notifications.duplicated'),
      action: this.i18n.translate('app.requisitions.notifications.duplicated_cta'),
    },
    archived: this.i18n.translate('app.requisitions.notifications.archived'),
    retrieved: this.i18n.translate('app.requisitions.notifications.retrieved.one'),
    deleted: this.i18n.translate('app.requisitions.notifications.deleted'),
    memberRemoved: this.i18n.translate('app.memberships.member_removed_notification'),
    flowUpdated: this.i18n.translate('app.requisitions.notifications.flow_updated'),
    openingsDeleted: (count: number) =>
      this.i18n.translate('app.requisitions.notifications.openings_deleted', { count }),
    candidateRemoved: {
      title: this.i18n.translate('app.requisitions.notifications.candidate_removed.title'),
      message: this.i18n.translate('app.requisitions.notifications.candidate_removed.message'),
    },
    reminderSent: this.i18n.translate('app.requisitions.notifications.reminder_sent'),
    approversUpdated: this.i18n.translate('app.requisitions.notifications.approvers_updated'),
    genericResponseError: this.i18n.translate('app.common.error_something_went_wrong'),
    fieldsChanged: {
      title: this.i18n.translate('app.requisitions.notifications.fields_changed.title'),
      message: this.i18n.translate('app.requisitions.notifications.fields_changed.message'),
    },
    fill_required_fields: this.i18n.translate(
      'app.requisitions.notifications.fill_required_fields',
    ),
  };

  constructor() {
    this.handleNotifications().pipe(takeUntil(this.ngOnDestroy$)).subscribe();
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public invalidFormFieldsError() {
    this.notifications.error({
      title: this.i18n.translate('app.offer_form.invalid_form_notification_title_v2'),
      message: this.i18n.translate('app.offer_form.invalid_form_notification_message_v2'),
    });
  }

  public saving() {
    this.notifications.neutral(this.i18n.translate('app.offer_form.job_is_saving_notification'));
  }

  public fillRequiredFields(hasViewButton = false, requisitionId?: number) {
    const toastRef = this.notifications.error({
      title: this.texts.fill_required_fields,
      ...(hasViewButton ? { topButtonLabel: this.i18n.translate('app.offers.view') } : {}),
    });

    if (hasViewButton && requisitionId) {
      toastRef
        .topButtonClick()
        .pipe(take(1), takeUntil(toastRef.afterClosed))
        .subscribe(() => {
          this.router.navigateByUrl(`/requisitions/${requisitionId}/details`);
        });
    }
  }

  public requisitionFieldsChanged() {
    this.notifications.error({
      title: this.texts.fieldsChanged.title,
      message: this.texts.fieldsChanged.message,
    });
  }

  private handleNotifications() {
    return merge(
      this.created$(),
      this.requisitionUpdated$(),
      this.requisitionUpdateFailed$(),
      this.approvalRequested$(),
      this.approvalRequestCanceled$(),
      this.requestApproved$(),
      this.requestRejected$(),
      this.titleUpdated$(),
      this.follow$(),
      this.unfollow$(),
      this.duplicated$(),
      this.archived$(),
      this.retrieved$(),
      this.memberRemoved$(),
      this.flowUpdated$(),
      this.openingsDeleted$(),
      this.candidateUnassigned$(),
      this.deleted$(),
      this.reminderSent$(),
      this.approversUpdated$(),
    );
  }

  private created$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.createOnePessimisticSuccess),
      tap(() => this.notifications.success(this.texts.savedAsDraft)),
    );
  }

  private requisitionUpdated$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.updateOnePessimisticSuccess),
      tap(({ payload: { context } }) => {
        if (context === UpdateContext.Default) this.notifications.success(this.texts.changesSaved);
      }),
    );
  }

  private requisitionUpdateFailed$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.updateOnePessimisticFailed),
      tap(() => {
        this.notifications.error(this.texts.genericResponseError);
      }),
    );
  }

  private approvalRequested$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.requestApprovalSuccess),
      tap(() => this.notifications.success(this.texts.approvalRequested)),
    );
  }

  private approvalRequestCanceled$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.cancelApprovalRequestSuccess),
      tap(() => this.notifications.success(this.texts.requestCanceled)),
    );
  }

  private requestApproved$() {
    return this.actions$.pipe(
      ofType(AssignedFlowsActions.approveRequestSuccess),
      tap(() => this.notifications.success(this.texts.requestApproved)),
    );
  }

  private requestRejected$() {
    return this.actions$.pipe(
      ofType(AssignedFlowsActions.rejectRequestSuccess),
      tap(() =>
        this.notifications.success({
          title: this.texts.requestRejected.title,
          message: this.texts.requestRejected.message,
        }),
      ),
    );
  }

  private titleUpdated$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.updateTitleSuccess),
      tap(() => this.notifications.success(this.texts.titleSaved)),
    );
  }

  private follow$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.followSuccess),
      tap(() =>
        this.notifications.success({
          title: this.texts.followTitle,
          message: this.texts.followDescription,
        }),
      ),
    );
  }

  private unfollow$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.unfollowSuccess),
      tap(() => this.notifications.success(this.texts.unfollowTitle)),
    );
  }

  private openingsDeleted$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.deleteOpenings),
      tap(({ payload }) =>
        this.notifications.success(this.texts.openingsDeleted(payload.openingsDeleted)),
      ),
    );
  }

  private duplicated$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.duplicateSuccess),
      tap(({ payload: { data, viewContext } }) =>
        viewContext === DuplicateViewContext.Dialog
          ? this.duplicatedContextDialog()
          : this.duplicatedContextPage(data),
      ),
    );
  }

  private duplicatedContextPage(data: { id: number }) {
    const toastRef = this.notifications.success({
      title: this.texts.duplicated.title,
      topButtonLabel: this.texts.duplicated.action,
    });
    const buttonClicked$ = toastRef
      .topButtonClick()
      .pipe(take(1))
      .subscribe(() => this.router.navigateByUrl(`/requisitions/${data.id}`));
    toastRef.afterClosed.pipe(take(1)).subscribe(() => {
      if (!buttonClicked$.closed) {
        buttonClicked$.unsubscribe();
      }
    });
  }

  private duplicatedContextDialog() {
    this.notifications.success(this.texts.duplicated.title);
  }

  private archived$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.archiveSuccess),
      tap(() => this.notifications.success(this.texts.archived)),
    );
  }

  private retrieved$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.retrieveSuccess),
      tap(() => this.notifications.success(this.texts.retrieved)),
    );
  }

  private deleted$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.removeOnePessimisticSuccess),
      tap(() => this.notifications.success(this.texts.deleted)),
    );
  }

  private memberRemoved$() {
    return this.actions$.pipe(
      ofType(RequisitionAssignmentsActions.removeOnePessimisticSuccess),
      tap(() => this.notifications.success(this.texts.memberRemoved)),
    );
  }

  private flowUpdated$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.setFlowSuccess),
      tap(action =>
        this.notifications.success({
          title: this.texts.flowUpdated,
          message: action.payload.flow.title,
        }),
      ),
    );
  }

  private candidateUnassigned$() {
    return this.actions$.pipe(
      ofType(RequisitionsActions.unassignCandidateSuccess),
      tap(() =>
        this.notifications.success({
          title: this.texts.candidateRemoved.title,
          message: this.texts.candidateRemoved.message,
        }),
      ),
    );
  }

  private reminderSent$() {
    return this.actions$.pipe(
      ofType(AssignedFlowsActions.sendReminderSuccess),
      tap(() => this.notifications.success(this.texts.reminderSent)),
    );
  }

  private approversUpdated$() {
    return this.actions$.pipe(
      ofType(AssignedFlowsActions.updateApproversSuccess),
      tap(() => this.notifications.success(this.texts.approversUpdated)),
    );
  }
}
